import React from 'react';
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Container,
  FormControl,
  Grid,
  Select,
  Stack,
} from '@mui/material';
import { LocalizedAlert as Alert, LocalizedInputLabel as InputLabel, LocalizedMenuItem as MenuItem, LocalizedTextField as TextField } from '../components/i18n/LocalizedMui';
import Typography from '../components/i18n/LocalizedTypography';
import { useNavigate } from 'react-router-dom';
import { useChart } from '../contexts/ChartContext';
import {
  buildCelebrityChartPayload,
  CelebrityProfile,
  fetchCelebrityProfiles,
  getBirthTimeParts,
} from '../utils/celebrityPractice';
import PremiumTrialPopup from '../components/PremiumTrialPopup';
import { useAuth } from '../contexts/AuthContext';
import { usePremiumFeatureAccess } from '../hooks/usePremiumFeatureAccess';

const CelebrityChartPractice: React.FC = () => {
  const navigate = useNavigate();
  const { calculateChart } = useChart();
  const { state: authState } = useAuth();
  const { popup, loading: trialLoading, checkFeatureAccess, closePopup } = usePremiumFeatureAccess();
  const [query, setQuery] = React.useState('');
  const [category, setCategory] = React.useState('all');
  const [profiles, setProfiles] = React.useState<CelebrityProfile[]>([]);
  const [categories, setCategories] = React.useState<string[]>([]);
  const [loadingProfiles, setLoadingProfiles] = React.useState(true);
  const [loadingSlug, setLoadingSlug] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [calculatedChartIds, setCalculatedChartIds] = React.useState<Record<string, string>>({});

  React.useEffect(() => {
    document.title = 'Practice Charts - Kundlistar Studio';
  }, []);

  React.useEffect(() => {
    let mounted = true;
    setLoadingProfiles(true);
    fetchCelebrityProfiles()
      .then((data) => {
        if (!mounted) return;
        setProfiles(data.profiles);
        setCategories(data.categories);
      })
      .catch((err: any) => {
        if (!mounted) return;
        setError(err.message || 'Unable to load practice charts.');
      })
      .finally(() => {
        if (mounted) setLoadingProfiles(false);
      });

    return () => {
      mounted = false;
    };
  }, []);

  const filteredProfiles = React.useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    return profiles.filter((profile) => {
      const matchesCategory = category === 'all' || profile.category === category;
      const haystack = [
        profile.displayName,
        profile.fullName,
        profile.category,
        profile.country,
        ...(profile.subCategories || [])
      ].join(' ').toLowerCase();
      return matchesCategory && (!normalizedQuery || haystack.includes(normalizedQuery));
    });
  }, [category, profiles, query]);

  const openProfileChart = async (profile: CelebrityProfile) => {
    if (!authState.isAuthenticated) {
      navigate('/login');
      return;
    }

    try {
      setError(null);
      setLoadingSlug(profile.slug);
      if (!getBirthTimeParts(profile)) {
        throw new Error('Birth time is not available for this chart yet.');
      }
      const existingChartId = calculatedChartIds[profile.slug];
      if (existingChartId) {
        navigate(`/chart/${existingChartId}`);
        return;
      }

      const payload = buildCelebrityChartPayload(profile);
      await checkFeatureAccess({
        featureKey: 'CHART_DATABASE',
        apiCall: async (trialAccessToken?: string) => {
          const chart = await calculateChart(payload, { trialAccessToken });
          return { data: { chart } } as any;
        },
        onSuccess: ({ chart }: any) => {
          const chartId = chart?._id || chart?.chartId;
          if (!chartId) {
            throw new Error('Chart calculated, but no chart id was returned.');
          }
          setCalculatedChartIds((prev) => ({ ...prev, [profile.slug]: chartId }));
          navigate(`/chart/${chartId}`);
        }
      });
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Unable to open chart.');
    } finally {
      setLoadingSlug(null);
    }
  };

  if (loadingProfiles) {
    return (
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Stack alignItems="center" spacing={2}>
          <CircularProgress />
          <Typography color="text.secondary">Loading practice charts...</Typography>
        </Stack>
      </Container>
    );
  }

  if (!profiles.length) {
    return (
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Alert severity={error ? 'error' : 'warning'}>{error || 'No chart profiles are available yet.'}</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Stack spacing={3}>
        <Box>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 800, color: 'primary.main' }}>
            Practice Charts
          </Typography>
        </Box>

        {error && <Alert severity="error">{error}</Alert>}

        <Grid container spacing={2.5} alignItems="flex-start">
          <Grid size={12}>
            <Stack spacing={2}>
              <Grid container spacing={1.5}>
                <Grid size={{ xs: 12, md: 7 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Search chart"
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 5 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Category</InputLabel>
                    <Select label="Category" value={category} onChange={(event) => setCategory(event.target.value)}>
                      <MenuItem value="all">All</MenuItem>
                      {categories.map((item) => (
                        <MenuItem key={item} value={item}>{item}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              <Box sx={{ maxHeight: { xs: 'none', md: 'calc(100vh - 260px)' }, overflowY: 'auto', pr: 0.5 }}>
                <Grid container spacing={1.5}>
                  {filteredProfiles.map((profile) => {
                    const isLoading = loadingSlug === profile.slug;
                    return (
                      <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={profile.slug}>
                        <Card
                          onClick={() => !loadingSlug && openProfileChart(profile)}
                          sx={{
                            cursor: loadingSlug ? 'wait' : 'pointer',
                            borderColor: isLoading ? 'primary.main' : 'divider',
                            bgcolor: isLoading ? 'rgba(46, 125, 82, 0.08)' : 'background.paper',
                            transition: 'border-color 0.15s ease, background-color 0.15s ease',
                            '&:hover': {
                              borderColor: 'primary.main',
                              bgcolor: 'rgba(46, 125, 82, 0.06)'
                            }
                          }}
                        >
                          <CardContent sx={{ minHeight: 74, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, p: 2, '&:last-child': { pb: 2 } }}>
                            <Typography sx={{ fontWeight: 850, color: 'primary.main' }}>{profile.displayName}</Typography>
                            {isLoading && <CircularProgress size={18} />}
                          </CardContent>
                        </Card>
                      </Grid>
                    );
                  })}
                </Grid>
              </Box>
            </Stack>
          </Grid>
        </Grid>
      </Stack>
      <PremiumTrialPopup
        open={popup.open}
        featureName={popup.featureName}
        used={popup.used}
        total={popup.total}
        remaining={popup.remaining}
        loading={trialLoading}
        onCancel={closePopup}
        onContinue={popup.nextAction}
      />
    </Container>
  );
};

export default CelebrityChartPractice;
